import type { Features } from '@/hooks/useSettings';
import { TEMPLATE_FORMAT } from '@/lib/noteStyle';
import type { OnboardingFormValues } from '@/pages/onboarding/onboarding.types';
import type { OnboardingProfile } from '@/types/onboarding';

export const DEFAULT_ONBOARDING_VALUES: OnboardingFormValues = {
  profession: null,
  professionOther: '',
  specialty: null,
  specialtyOther: '',
  consultationTypes: [],
  sessionMode: null,
  noteDetail: 'balanced',
  noteFormat: TEMPLATE_FORMAT,
  letterSignature: '',
};

/** The person's current note style: completion overwrites it, and Settings can change it after onboarding. */
const currentNoteStyle = (features: Features) => ({
  noteDetail: features.default_note_detail ?? DEFAULT_ONBOARDING_VALUES.noteDetail,
  noteFormat: features.default_note_format ?? DEFAULT_ONBOARDING_VALUES.noteFormat,
});

/** First run: keep the note style and signature the person already has. */
const firstRunValues = (features: Features): OnboardingFormValues => ({
  ...DEFAULT_ONBOARDING_VALUES,
  ...currentNoteStyle(features),
  letterSignature: typeof features.letter_signature === 'string' ? features.letter_signature : '',
});

/** Pre-fill from saved answers (edit mode from Settings), else from current settings. Note style is always current. */
export const formValuesFromProfile = (
  profile: OnboardingProfile | null,
  features: Features = {}
): OnboardingFormValues =>
  profile
    ? {
        profession: profile.profession,
        professionOther: profile.professionOther ?? '',
        specialty: profile.specialty ?? null,
        specialtyOther: profile.specialtyOther ?? '',
        consultationTypes: profile.consultationTypes,
        sessionMode: profile.sessionMode,
        ...currentNoteStyle(features),
        letterSignature: profile.letterSignature ?? '',
      }
    : firstRunValues(features);

const hasText = (value: string) => value.trim().length > 0;

/** Whether the step's required answers are complete (gates "Áfram"). */
export const isStepValid = (step: number, values: OnboardingFormValues): boolean => {
  switch (step) {
    case 1: {
      if (!values.profession) return false;
      if (values.profession === 'other') return hasText(values.professionOther);
      if (values.profession !== 'doctor') return true;
      if (!values.specialty) return false;
      return values.specialty !== 'other' || hasText(values.specialtyOther);
    }
    case 2:
      return values.consultationTypes.length > 0 && values.sessionMode !== null;
    default:
      return true;
  }
};

/**
 * Form values → saved profile. Optional keys are only set when they apply
 * (e.g. `specialty` only for doctors). The signature is sent only when edited,
 * so a pre-fill that failed to load never clears the saved one; an emptied one becomes null.
 */
export const toOnboardingProfile = (values: OnboardingFormValues, signatureEdited = false): OnboardingProfile => {
  if (!values.profession || !values.sessionMode) throw new Error('Onboarding answers are incomplete');
  const isDoctor = values.profession === 'doctor';
  return {
    profession: values.profession,
    ...(values.profession === 'other' && { professionOther: values.professionOther.trim() }),
    ...(isDoctor && values.specialty && { specialty: values.specialty }),
    ...(isDoctor && values.specialty === 'other' && { specialtyOther: values.specialtyOther.trim() }),
    consultationTypes: values.consultationTypes,
    sessionMode: values.sessionMode,
    noteDetail: values.noteDetail,
    noteFormat: values.noteFormat === TEMPLATE_FORMAT ? null : values.noteFormat,
    ...(signatureEdited && {
      letterSignature: hasText(values.letterSignature) ? values.letterSignature.trim() : null,
    }),
  };
};
